import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Globe, MapPin, Search, Filter, Plus, X, Building2, User, ExternalLink, Linkedin, Loader2, BadgeCheck, Lock, Check, Crown, MessageSquare, Mail, Instagram, Users } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { ACTUATION_TYPES, BIM_LEVELS, SPECIALIZATIONS, SOFTWARE, LEVEL_COLORS } from './bimmapConstants';
import { PERSON_ICON, COMPANY_ICON } from './leafletSetup';
import RegisterProfileModal from './RegisterProfileModal';
import WaitlistModal from './WaitlistModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREMIUM_OFFER = 'K106882808Y';
// Vire para `true` quando o BIM Connect for lançado (mostra o botão "Ver perfil no BIM Connect").
const BIM_CONNECT_ENABLED = true;

const normUrl = (v) => (/^https?:\/\//i.test(v) ? v : `https://${v}`);
const igUrl = (v) => (/^https?:\/\//i.test(v) ? v : `https://instagram.com/${v.replace(/^@/, '')}`);

const PremiumContacts = ({ p, variant = 'dark' }) => {
  const digits = (p.phone || '').replace(/\D/g, '');
  const base = variant === 'light' ? 'text-gray-700 hover:text-red-600' : 'text-gray-300 hover:text-red-400';
  const rows = [];
  if (p.email) rows.push(<a key="email" href={`mailto:${p.email}`} className={`flex items-center gap-1.5 ${base}`} data-testid="bimmap-contact-email"><Mail size={12} className="shrink-0" /> {p.email}</a>);
  if (p.phone) rows.push(<a key="phone" href={digits ? `https://wa.me/${digits}` : '#'} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 ${variant === 'light' ? 'text-gray-700 hover:text-green-600' : 'text-gray-300 hover:text-green-400'}`} data-testid="bimmap-contact-phone"><MessageSquare size={12} className="shrink-0" /> {p.phone}</a>);
  if (p.website) rows.push(<a key="site" href={normUrl(p.website)} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 ${base}`} data-testid="bimmap-contact-site"><ExternalLink size={12} className="shrink-0" /> Site</a>);
  if (p.instagram) rows.push(<a key="ig" href={igUrl(p.instagram)} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 ${base}`} data-testid="bimmap-contact-instagram"><Instagram size={12} className="shrink-0" /> Instagram</a>);
  if (p.linkedin) rows.push(<a key="li" href={normUrl(p.linkedin)} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 ${variant === 'light' ? 'text-gray-700 hover:text-sky-600' : 'text-gray-300 hover:text-sky-400'}`} data-testid="bimmap-contact-linkedin"><Linkedin size={12} className="shrink-0" /> LinkedIn</a>);
  if (rows.length === 0) return null;
  return (
    <div className={`mt-2 space-y-1 text-[11px] ${variant === 'light' ? 'border-t border-gray-200 pt-2' : 'border-t border-zinc-800 pt-2'}`} onClick={(e) => e.stopPropagation()} data-testid="bimmap-contacts">
      {rows}
    </div>
  );
};

function MapRefSetter({ onReady }) {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  return null;
}

const BIMMapPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [premium, setPremium] = useState(false);
  const [hasConnect, setHasConnect] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ q: '', actuation: '', bim_level: '', specialization: '', software: '', entity_type: '', verified_only: false });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bimmap/profiles`);
      setProfiles(data);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMine = useCallback(async () => {
    if (!isAuthenticated) { setMyProfile(null); return; }
    try {
      const { data } = await axios.get(`${API}/bimmap/me`);
      setMyProfile(data || null);
    } catch { setMyProfile(null); }
  }, [isAuthenticated]);

  const fetchEntitlement = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bimmap/entitlement`);
      setPremium(!!data?.premium);
    } catch { setPremium(false); }
  }, [isAuthenticated]);

  const fetchConnect = useCallback(async () => {
    if (!isAuthenticated || !user?.id) { setHasConnect(null); return; }
    try {
      const { data } = await axios.get(`${API}/connect/profiles/user/${user.id}`);
      setHasConnect(!!data?.exists);
    } catch { setHasConnect(null); }
  }, [isAuthenticated, user]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles, premium]);
  useEffect(() => { fetchMine(); }, [fetchMine]);
  useEffect(() => { fetchEntitlement(); }, [fetchEntitlement]);
  useEffect(() => { fetchConnect(); }, [fetchConnect]);

  const filtered = useMemo(() => {
    return profiles.filter((p) => {
      if (filters.verified_only && !p.verified) return false;
      if (filters.entity_type && p.entity_type !== filters.entity_type) return false;
      if (filters.actuation && p.actuation !== filters.actuation) return false;
      if (filters.bim_level && p.bim_level !== filters.bim_level) return false;
      if (filters.specialization && !(p.specializations || []).includes(filters.specialization)) return false;
      if (filters.software && !(p.software || []).includes(filters.software)) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${p.name} ${p.company} ${p.headline} ${p.city} ${p.country}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [profiles, filters]);

  const flyTo = (p) => {
    if (map && p.lat != null) {
      map.flyTo([p.lat, p.lng], 11, { duration: 1.5 });
    }
  };

  const clearFilters = () => setFilters({ q: '', actuation: '', bim_level: '', specialization: '', software: '', entity_type: '', verified_only: false });
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v).length;

  const selCls = 'w-full bg-zinc-800 border border-zinc-700 rounded-md h-9 px-2 text-sm text-gray-200';

  return (
    <div className="min-h-screen bg-black">
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-10 bg-gradient-to-b from-zinc-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-red-600 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/30 text-red-300 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-4">
            <Globe size={14} /> Novo · Gratuito
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">AJ | BIM <span className="text-red-500">Map</span></h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto mb-8">
            O mapa mundial da comunidade BIM. Encontre profissionais e empresas, conheça suas qualificações técnicas e coloque-se no mapa.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button onClick={() => setModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white h-12 px-6 text-base" data-testid="bimmap-open-register">
              <Plus size={18} className="mr-2" />
              {myProfile ? 'Editar meu perfil no mapa' : 'Cadastre-se gratuitamente'}
            </Button>
            <div className="text-gray-400 text-sm flex items-center gap-2">
              <MapPin size={16} className="text-red-500" />
              <strong className="text-white">{profiles.length}</strong> no mapa
            </div>
          </div>
        </div>
      </section>

      {BIM_CONNECT_ENABLED && myProfile && hasConnect === false && (
        <div className="container mx-auto px-4 lg:px-8 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gradient-to-r from-orange-600/20 to-red-600/10 border border-orange-500/30 rounded-xl p-4" data-testid="bimmap-connect-cta">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 text-orange-300 flex items-center justify-center shrink-0"><Users size={20} /></div>
              <div>
                <div className="text-white font-semibold text-sm">Você já está no mapa — leve seu perfil além!</div>
                <div className="text-gray-300 text-xs mt-0.5">Crie seu perfil no <strong className="text-white">AJ | BIM Connect</strong> e conecte-se com a maior rede BIM da América Latina.</div>
              </div>
            </div>
            <Button onClick={() => navigate('/connect/create-profile')} className="bg-orange-600 hover:bg-orange-700 text-white shrink-0" data-testid="bimmap-connect-cta-btn">
              <Users size={16} className="mr-2" /> Criar perfil no BIM Connect
            </Button>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="container mx-auto px-4 lg:px-8 py-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Buscar por nome, empresa, cidade..." className="bg-zinc-900 border-zinc-700 text-white h-10 pl-9" data-testid="bimmap-search" />
          </div>
          <Button variant="outline" onClick={() => setShowFilters((s) => !s)} className="border-zinc-700 text-gray-200 hover:bg-zinc-800 h-10" data-testid="bimmap-toggle-filters">
            <Filter size={16} className="mr-2" /> Filtros {activeFilterCount > 0 && <Badge className="ml-2 bg-red-600">{activeFilterCount}</Badge>}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" onClick={clearFilters} className="text-gray-400 hover:text-white h-10" data-testid="bimmap-clear-filters"><X size={16} className="mr-1" /> Limpar</Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid="bimmap-filters-panel">
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Tipo</label>
              <select value={filters.entity_type} onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))} className={selCls} data-testid="bimmap-filter-entity">
                <option value="">Todos</option><option value="person">Pessoas</option><option value="company">Empresas</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Atuação</label>
              <select value={filters.actuation} onChange={(e) => setFilters((f) => ({ ...f, actuation: e.target.value }))} className={selCls} data-testid="bimmap-filter-actuation">
                <option value="">Todas</option>{ACTUATION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Nível BIM</label>
              <select value={filters.bim_level} onChange={(e) => setFilters((f) => ({ ...f, bim_level: e.target.value }))} className={selCls} data-testid="bimmap-filter-level">
                <option value="">Todos</option>{BIM_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Especialização</label>
              <select value={filters.specialization} onChange={(e) => setFilters((f) => ({ ...f, specialization: e.target.value }))} className={selCls} data-testid="bimmap-filter-spec">
                <option value="">Todas</option>{SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Software</label>
              <select value={filters.software} onChange={(e) => setFilters((f) => ({ ...f, software: e.target.value }))} className={selCls} data-testid="bimmap-filter-software">
                <option value="">Todos</option>{SOFTWARE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="col-span-2 md:col-span-5 flex items-center gap-2 text-sm text-gray-300 cursor-pointer mt-1">
              <input type="checkbox" checked={filters.verified_only} onChange={(e) => setFilters((f) => ({ ...f, verified_only: e.target.checked }))} className="accent-sky-500 w-4 h-4" data-testid="bimmap-filter-verified" />
              <BadgeCheck size={15} className="text-sky-400" /> Somente perfis verificados
            </label>
          </div>
        )}

        {/* Mapa + Lista */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-[560px] rounded-2xl overflow-hidden border border-zinc-800" data-testid="bimmap-map">
            <MapContainer center={[10, -20]} zoom={2} style={{ height: '100%', width: '100%' }} worldCopyJump>
              <MapRefSetter onReady={setMap} />
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {filtered.filter((p) => p.lat != null && p.lng != null).map((p) => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={p.entity_type === 'company' ? COMPANY_ICON : PERSON_ICON}>
                  <Popup>
                    <div className="min-w-[180px]">
                      <div className="font-bold text-sm">{p.name}</div>
                      {p.company && <div className="text-xs text-gray-600">{p.company}</div>}
                      {p.verified && <div className="text-[11px] text-sky-600 font-semibold mt-0.5">✓ Perfil verificado</div>}
                      <div className="text-xs mt-1">{p.actuation} · <strong>{p.bim_level}</strong></div>
                      {p.headline && <div className="text-xs text-gray-700 mt-1 italic">{p.headline}</div>}
                      <div className="text-[11px] text-gray-500 mt-1">{p.city}{p.country ? `, ${p.country}` : ''}</div>
                      {premium ? (
                        <PremiumContacts p={p} variant="light" />
                      ) : (
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          <Lock size={11} /> Contato disponível no Plano Premium
                        </div>
                      )}
                      {BIM_CONNECT_ENABLED && p.connect_profile_id && (
                        <button
                          type="button"
                          onClick={() => navigate(`/connect/profile/${p.connect_profile_id}`)}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded px-2 py-1.5 transition-colors"
                          data-testid="bimmap-connect-link-popup"
                        >
                          <ExternalLink size={12} /> Ver perfil no BIM Connect
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Lista */}
          <div className="h-[560px] overflow-y-auto pr-1 space-y-3" data-testid="bimmap-list">
            <div className="text-sm text-gray-400 mb-1">{filtered.length} resultado(s)</div>
            {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-red-500" size={28} /></div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-10 text-gray-500 text-sm">
                Nenhum perfil encontrado.<br />Seja o primeiro a se cadastrar!
              </div>
            )}
            {filtered.map((p) => (
              <div key={p.id} onClick={() => flyTo(p)} role="button" tabIndex={0} className="w-full text-left bg-zinc-900 border border-zinc-800 hover:border-red-500/60 rounded-xl p-4 transition-colors cursor-pointer" data-testid={`bimmap-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${p.entity_type === 'company' ? 'bg-red-600/20 text-red-400' : 'bg-orange-600/20 text-orange-400'}`}>
                      {p.entity_type === 'company' ? <Building2 size={20} /> : <User size={20} />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 min-w-0">
                        <span className="font-semibold text-white text-sm truncate">{p.name}</span>
                        {p.verified && <BadgeCheck size={14} className="text-sky-400 shrink-0" />}
                      </div>
                      <Badge className={`text-[10px] ${LEVEL_COLORS[p.bim_level] || 'bg-zinc-700'}`}>{p.bim_level}</Badge>
                    </div>
                    {p.company && <div className="text-xs text-gray-400 truncate">{p.company}</div>}
                    <div className="text-xs text-red-400 mt-0.5">{p.actuation}</div>
                    {p.headline && <div className="text-xs text-gray-400 mt-1 line-clamp-2">{p.headline}</div>}
                    <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-1"><MapPin size={11} /> {p.city}{p.country ? `, ${p.country}` : ''}</div>
                    {(p.software || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.software.slice(0, 4).map((s) => <span key={s} className="text-[10px] bg-zinc-800 text-gray-300 rounded px-1.5 py-0.5">{s}</span>)}
                      </div>
                    )}
                    {premium && <PremiumContacts p={p} variant="dark" />}
                    {BIM_CONNECT_ENABLED && p.connect_profile_id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/connect/profile/${p.connect_profile_id}`); }}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded px-2.5 py-1.5 transition-colors"
                        data-testid={`bimmap-connect-link-${p.id}`}
                      >
                        <ExternalLink size={12} /> Ver perfil no BIM Connect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Planos de acesso */}
      <section className="container mx-auto px-4 lg:px-8 py-14" data-testid="bimmap-plans">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Planos de acesso ao BIMMap</h2>
          <p className="text-gray-400 mt-2 max-w-2xl mx-auto">Apareça no mapa gratuitamente. Para <strong>conectar-se</strong> com profissionais e empresas, assine o Plano Premium.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Gratuito */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-7 flex flex-col" data-testid="bimmap-plan-free">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="text-gray-300" size={20} />
              <h3 className="text-xl font-bold text-white">Gratuito</h3>
            </div>
            <div className="text-3xl font-bold text-white mb-1">R$ 0</div>
            <p className="text-xs text-gray-500 mb-5">Para sempre</p>
            <ul className="space-y-2.5 flex-1">
              {['Apareça no mapa mundial da comunidade BIM', 'Perfil com qualificação técnica (nível, especializações e softwares)', 'Navegue e filtre profissionais e empresas', 'Veja o mini-resumo de cada perfil', 'Selo de perfil verificado'].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-300"><Check size={16} className="text-green-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
              <li className="flex items-start gap-2 text-sm text-gray-500"><Lock size={15} className="mt-0.5 shrink-0" /> Contatos (e-mail, telefone/WhatsApp, site, Instagram, LinkedIn) <strong className="text-gray-400">bloqueados</strong></li>
            </ul>
            <Button onClick={() => setModalOpen(true)} variant="outline" className="mt-6 w-full border-zinc-600 text-white hover:bg-zinc-800" data-testid="bimmap-plan-free-cta">
              {myProfile ? 'Editar meu perfil' : 'Cadastrar gratuitamente'}
            </Button>
          </div>

          {/* Premium */}
          <div className="relative overflow-hidden bg-gradient-to-br from-red-900/30 via-zinc-900 to-zinc-900 border-2 border-red-500 rounded-2xl p-7 flex flex-col" data-testid="bimmap-plan-premium">
            <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none overflow-hidden" data-testid="bimmap-premium-ribbon">
              <div className="absolute bg-green-600 text-white text-[11px] font-bold tracking-wider text-center shadow-lg" style={{ width: '170px', transform: 'rotate(45deg)', right: '-48px', top: '30px', padding: '5px 0' }}>
                DISPONÍVEL
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="text-red-400" size={20} />
              <h3 className="text-xl font-bold text-white">Premium</h3>
            </div>
            <div className="mb-1"><span className="text-4xl font-bold text-white">R$ 79</span><span className="text-gray-400">/ano</span></div>
            <p className="text-xs text-gray-400 mb-5">À vista ou em até <strong className="text-gray-300">12x de R$ 6,58</strong> · cartão de crédito ou PayPal</p>
            <ul className="space-y-2.5 flex-1">
              <li className="flex items-start gap-2 text-sm text-gray-200"><Check size={16} className="text-green-400 mt-0.5 shrink-0" /> Tudo do plano Gratuito, mais:</li>
              {['Acesso ao e-mail de contato', 'Telefone direto / WhatsApp', 'Site, Instagram e LinkedIn', 'Conecte-se com profissionais e empresas do mundo todo'].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white"><Check size={16} className="text-red-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
              <li className="flex items-start gap-2 text-sm text-white"><Crown size={15} className="text-red-400 mt-0.5 shrink-0" /> Acesso ao <strong>AJ | BIM Connect</strong> (rede profissional) — em breve</li>
            </ul>
            <Button
              onClick={() => window.open(`https://pay.hotmart.com/${PREMIUM_OFFER}`, '_blank', 'noopener')}
              disabled={premium}
              className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white h-11 text-base disabled:opacity-70 disabled:cursor-default"
              data-testid="bimmap-plan-premium-cta"
            >
              <Crown size={18} className="mr-2" />
              {premium ? 'Premium ativo · contatos liberados' : 'Assinar Premium · R$ 79/ano'}
            </Button>
            {!premium && (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-2.5 py-2" data-testid="bimmap-premium-email-note">
                <Mail size={13} className="mt-0.5 shrink-0" />
                <span><strong>Importante:</strong> use na compra o <strong>mesmo e-mail</strong> da sua conta no AJ | BIM. Assim a liberação é automática. Ainda não tem conta? Cadastre-se com esse e-mail depois e o acesso é liberado no primeiro login.</span>
              </p>
            )}
            {!premium && (
              <button
                type="button"
                onClick={() => setWaitlistOpen(true)}
                className="mt-2 text-xs text-gray-400 hover:text-white underline underline-offset-2"
                data-testid="bimmap-plan-premium-notify"
              >
                Prefere receber novidades antes? Entrar na lista
              </button>
            )}
          </div>
        </div>
      </section>

      <RegisterProfileModal open={modalOpen} onOpenChange={setModalOpen} existing={myProfile} onSaved={() => { fetchProfiles(); fetchMine(); }} />
      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} />
    </div>
  );
};

export default BIMMapPage;
